import React from "react";
import { Box, Chip, Typography, alpha } from "@mui/material";
import { LocalOffer, RestaurantMenu as RestaurantMenuIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { LUXURY } from "data/guestLuxuryTheme";

const pillSx = (active) => ({
    appearance: "none",
    border: active ? `1px solid ${LUXURY.gold}` : `1px solid ${LUXURY.cardBorder}`,
    flex: "0 0 auto",
    minHeight: 42,
    px: 1.45,
    borderRadius: 999,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.7,
    bgcolor: active ? LUXURY.gold : LUXURY.card,
    color: active ? LUXURY.forestDeep : LUXURY.muted,
    fontFamily: LUXURY.bodyFont,
    boxShadow: "none",
    transition: "background 0.18s ease, color 0.18s ease",
    WebkitTapHighlightColor: "transparent",
});

const CategoryNavigationBar = ({
    luxury = false,
    offerItems,
    onOpenOffers,
    selectedCategory,
    onSelectCategory,
    menuData,
    isMobile,
    dietaryFilter,
    onChangeDietaryFilter,
    onClearAllFilters,
}) => {
    const theme = useTheme();

    if (luxury) {
        return (
            <Box
                sx={{
                    bgcolor: "transparent",
                    py: { xs: 1.1, md: 1.5 },
                    px: { xs: 2, md: 4 },
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        maxWidth: { xs: LUXURY.maxWidth, md: "100%" },
                        mx: "auto",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.8,
                            alignItems: "center",
                            overflowX: "auto",
                            overflowY: "hidden",
                            mx: -0.25,
                            px: 0.25,
                            py: 0.15,
                            mb: { xs: 0, md: 1 },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        {offerItems.length > 0 && (
                            <Box
                                component="button"
                                type="button"
                                onClick={onOpenOffers}
                                sx={{
                                    ...pillSx(false),
                                    bgcolor: LUXURY.gold,
                                    color: LUXURY.forest,
                                    border: `1px solid ${LUXURY.gold}`,
                                    fontWeight: 800,
                                    fontSize: "0.78rem",
                                }}
                            >
                                <LocalOffer sx={{ fontSize: 16 }} />
                                Offers · {offerItems.length}
                            </Box>
                        )}

                        <Box
                            component="button"
                            type="button"
                            onClick={() => onSelectCategory("all")}
                            sx={pillSx(selectedCategory === "all")}
                        >
                            <RestaurantMenuIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.01em" }}>
                                All
                            </Typography>
                        </Box>

                        {menuData.map((category) => {
                            const isActive = selectedCategory === category.id;
                            const itemCount =
                                category.MenuItems?.filter((item) => item.isActive).length || 0;

                            return (
                                <Box
                                    key={category.id}
                                    component="button"
                                    type="button"
                                    onClick={() => onSelectCategory(category.id)}
                                    sx={pillSx(isActive)}
                                >
                                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, lineHeight: 1, letterSpacing: "-0.01em" }}>
                                        {category.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            minWidth: 18,
                                            height: 18,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "0.62rem",
                                            fontWeight: 800,
                                            bgcolor: isActive ? "rgba(42,36,28,0.12)" : "rgba(42,36,28,0.06)",
                                            color: LUXURY.ink,
                                            px: 0.5,
                                        }}
                                    >
                                        {itemCount}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {!isMobile && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 0.8,
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: LUXURY.hint,
                                    fontWeight: 800,
                                    fontSize: "0.64rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.22em",
                                }}
                            >
                                Filters
                            </Typography>

                            {[
                                { key: "vegetarian", label: "Vegetarian" },
                                { key: "vegan", label: "Vegan" },
                                { key: "spicy", label: "Spicy" },
                            ].map((diet) => {
                                const active = dietaryFilter.includes(diet.key);
                                return (
                                    <Box
                                        key={diet.key}
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            onChangeDietaryFilter((prev) =>
                                                prev.includes(diet.key)
                                                    ? prev.filter((f) => f !== diet.key)
                                                    : [...prev, diet.key],
                                            );
                                        }}
                                        sx={pillSx(active)}
                                    >
                                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, lineHeight: 1 }}>
                                            {diet.label}
                                        </Typography>
                                    </Box>
                                );
                            })}

                            {(dietaryFilter.length > 0 || selectedCategory !== "all") && (
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={onClearAllFilters}
                                    sx={{
                                        ...pillSx(false),
                                        color: LUXURY.orangeDark,
                                        border: `1px solid ${alpha(LUXURY.orange, 0.35)}`,
                                    }}
                                >
                                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, lineHeight: 1 }}>
                                        Clear
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                bgcolor: alpha("#ffffff", 0.95),
                py: { xs: 1, md: 1.5 },
                px: { xs: 1.5, md: 4 },
                flexShrink: 0,
                boxShadow: `0 5px 16px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: 0.75,
                    alignItems: "center",
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 0,
                    mb: { xs: 0, md: 1 },
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {offerItems.length > 0 && (
                    <Chip
                        icon={<LocalOffer sx={{ fontSize: 14 }} />}
                        label={`Offers · ${offerItems.length}`}
                        onClick={onOpenOffers}
                        size="small"
                        sx={{
                            flexShrink: 0,
                            px: 1.75,
                            height: 36,
                            borderRadius: 2,
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                            color: "#fff",
                            boxShadow: "0 2px 10px rgba(245,158,11,0.3)",
                            cursor: "pointer",
                            "& .MuiChip-icon": { color: "#fff" },
                            "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 16px rgba(245,158,11,0.45)",
                            },
                            transition: "all 0.2s ease-in-out",
                        }}
                    />
                )}

                <Chip
                    icon={<RestaurantMenuIcon sx={{ fontSize: 15 }} />}
                    label="All"
                    onClick={() => onSelectCategory("all")}
                    size="small"
                    sx={{
                        flexShrink: 0,
                        px: 1.5,
                        height: 36,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        border:
                            selectedCategory === "all"
                                ? "none"
                                : `1.5px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                        background:
                            selectedCategory === "all"
                                ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                                : alpha(theme.palette.primary.main, 0.04),
                        color: selectedCategory === "all" ? "#fff" : theme.palette.primary.main,
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        boxShadow:
                            selectedCategory === "all"
                                ? `0 3px 10px ${alpha(theme.palette.primary.main, 0.3)}`
                                : "none",
                        "& .MuiChip-icon": {
                            color: selectedCategory === "all" ? "#fff" : theme.palette.primary.main,
                            fontSize: 15,
                            ml: 0.5,
                        },
                        "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor:
                                selectedCategory === "all"
                                    ? undefined
                                    : alpha(theme.palette.primary.main, 0.08),
                        },
                    }}
                />

                {menuData.map((category) => {
                    const isActive = selectedCategory === category.id;
                    const itemCount =
                        category.MenuItems?.filter((item) => item.isActive).length || 0;

                    return (
                        <Chip
                            key={category.id}
                            label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                                        {category.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            minWidth: 18,
                                            height: 18,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "0.65rem",
                                            fontWeight: 700,
                                            bgcolor: isActive
                                                ? alpha("#fff", 0.3)
                                                : alpha(theme.palette.primary.main, 0.12),
                                            color: isActive ? "#fff" : theme.palette.primary.main,
                                            px: 0.5,
                                        }}
                                    >
                                        {itemCount}
                                    </Box>
                                </Box>
                            }
                            onClick={() => onSelectCategory(category.id)}
                            size="small"
                            sx={{
                                flexShrink: 0,
                                px: 1.5,
                                height: 36,
                                borderRadius: 2,
                                cursor: "pointer",
                                background: isActive
                                    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                                    : alpha(theme.palette.primary.main, 0.04),
                                color: isActive ? "#fff" : theme.palette.text.secondary,
                                border: isActive
                                    ? "none"
                                    : `1.5px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                                fontWeight: 600,
                                transition: "all 0.2s ease-in-out",
                                boxShadow: isActive
                                    ? `0 3px 10px ${alpha(theme.palette.primary.main, 0.3)}`
                                    : "none",
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    bgcolor: isActive
                                        ? undefined
                                        : alpha(theme.palette.primary.main, 0.08),
                                },
                            }}
                        />
                    );
                })}
            </Box>

            {!isMobile && (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.disabled",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                        }}
                    >
                        Filters
                    </Typography>

                    <Box
                        onClick={() => {
                            onChangeDietaryFilter((prev) =>
                                prev.includes("vegetarian")
                                    ? prev.filter((f) => f !== "vegetarian")
                                    : [...prev, "vegetarian"],
                            );
                        }}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            transition: "all 0.2s ease-in-out",
                            border: `1.5px solid ${dietaryFilter.includes("vegetarian")
                                ? theme.palette.success.main
                                : alpha(theme.palette.divider, 0.3)
                                }`,
                            bgcolor: dietaryFilter.includes("vegetarian")
                                ? alpha(theme.palette.success.main, 0.12)
                                : "transparent",
                            color: dietaryFilter.includes("vegetarian")
                                ? theme.palette.success.dark
                                : "text.secondary",
                            "&:hover": {
                                borderColor: theme.palette.success.main,
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                transform: "translateY(-1px)",
                            },
                        }}
                    >
                        Vegetarian
                    </Box>

                    <Box
                        onClick={() => {
                            onChangeDietaryFilter((prev) =>
                                prev.includes("vegan")
                                    ? prev.filter((f) => f !== "vegan")
                                    : [...prev, "vegan"],
                            );
                        }}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            transition: "all 0.2s ease-in-out",
                            border: `1.5px solid ${dietaryFilter.includes("vegan")
                                ? theme.palette.success.main
                                : alpha(theme.palette.divider, 0.3)
                                }`,
                            bgcolor: dietaryFilter.includes("vegan")
                                ? alpha(theme.palette.success.main, 0.12)
                                : "transparent",
                            color: dietaryFilter.includes("vegan")
                                ? theme.palette.success.dark
                                : "text.secondary",
                            "&:hover": {
                                borderColor: theme.palette.success.main,
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                transform: "translateY(-1px)",
                            },
                        }}
                    >
                        Vegan
                    </Box>

                    <Box
                        onClick={() => {
                            onChangeDietaryFilter((prev) =>
                                prev.includes("spicy")
                                    ? prev.filter((f) => f !== "spicy")
                                    : [...prev, "spicy"],
                            );
                        }}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            transition: "all 0.2s ease-in-out",
                            border: `1.5px solid ${dietaryFilter.includes("spicy")
                                ? theme.palette.error.main
                                : alpha(theme.palette.divider, 0.3)
                                }`,
                            bgcolor: dietaryFilter.includes("spicy")
                                ? alpha(theme.palette.error.main, 0.12)
                                : "transparent",
                            color: dietaryFilter.includes("spicy")
                                ? theme.palette.error.dark
                                : "text.secondary",
                            "&:hover": {
                                borderColor: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                transform: "translateY(-1px)",
                            },
                        }}
                    >
                        Spicy
                    </Box>

                    {(dietaryFilter.length > 0 || selectedCategory !== "all") && (
                        <Box
                            onClick={onClearAllFilters}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 1.5,
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                ml: 0.5,
                                transition: "all 0.2s ease-in-out",
                                border: `1.5px solid ${theme.palette.error.main}`,
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                color: theme.palette.error.dark,
                                "&:hover": {
                                    bgcolor: alpha(theme.palette.error.main, 0.2),
                                    transform: "translateY(-1px)",
                                },
                            }}
                        >
                            Clear
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default CategoryNavigationBar;
